import md5 from 'blueimp-md5'
import React, { ComponentProps, useState } from 'react'
import { cn } from '../lib/utils'

type UserAvatarProps = {
  email?: string
  initials?: string
  className?: string
  backgroundColor?: string
} & ComponentProps<'div'>

// Predefined color palette for consistent avatar backgrounds
// Based on provided colors and complementary hues, all with sufficient contrast for white text
const avatarColors = [
  '#F0926C', // Coral (provided)
  '#F2BA94', // Peach (provided)
  '#FCF2AD', // Cream (provided) - will need dark text
  '#ABBAEB', // Lavender Blue (provided)
  '#F9A9F0', // Pink (provided)
  '#68C196', // Mint Green (provided)
  '#8B5A2B', // Brown (complementary to cream)
  '#4A5568', // Slate Gray (complementary to lavender)
  '#2D3748', // Dark Gray (complementary to pink)
  '#22543D', // Dark Green (complementary to mint)
  '#742A2A', // Dark Red (complementary to coral)
  '#744210', // Dark Orange (complementary to peach)
  '#553C9A', // Purple (complementary to lavender)
  '#2C7A7B', // Teal (complementary to mint)
  '#C53030', // Red (complementary to green)
  '#D69E2E', // Yellow (complementary to purple)
  '#38A169', // Green (complementary to red)
  '#3182CE', // Blue (complementary to orange)
  '#805AD5', // Violet (complementary to yellow)
  '#E53E3E', // Bright Red (complementary to green)
]

/**
 * Generates a consistent background color for a user based on their email address
 * @param email - The user's email address
 * @returns A hex color string
 */
function getAvatarColor(email: string): string {
  if (!email) return avatarColors[0]

  // Create a hash from the email
  const hash = md5(email.trim().toLowerCase())

  // Convert the first 6 characters of the hash to a number
  const hashNumber = parseInt(hash.substring(0, 6), 16)

  // Use modulo to get a consistent index from the color array
  const colorIndex = hashNumber % avatarColors.length

  return avatarColors[colorIndex]
}

// Function to determine if a color is light (needs dark text)
function isLightColor(color: string): boolean {
  // Convert hex to RGB
  const hex = color.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  return luminance > 0.7
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  email,
  initials = 'U',
  className = '',
  backgroundColor,
}) => {
  const [error, setError] = useState(false)
  const gravatarHash = email ? md5(email.trim().toLowerCase()) : ''
  const gravatarUrl = email
    ? `https://www.gravatar.com/avatar/${gravatarHash}?s=64&d=404`
    : ''

  if (email && !error) {
    return (
      <img
        src={gravatarUrl}
        alt={initials}
        className={`rounded-full object-cover ${className}`}
        onError={() => setError(true)}
      />
    )
  }

  const bgColor = backgroundColor || getAvatarColor(email || '')
  const textColor = isLightColor(bgColor) ? '#1F2937' : '#FFFFFF'

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full text-sm font-medium',
        className
      )}
      style={{
        backgroundColor: bgColor,
        color: textColor,
      }}
    >
      {initials}
    </div>
  )
}

export default UserAvatar
